import { useState } from 'react';
import { User, Clock, ChevronDown, ChevronUp, ImageIcon, CheckCircle, XCircle, MessageSquare, Loader2, AlertTriangle, X } from 'lucide-react';
import { useAcceptBidMutation, useRejectBidMutation, useWithdrawBidMutation } from '@nimbazaar/store';
import { useToast } from './Toast';

interface BidImage {
  id: string;
  url: string;
  sortOrder: number;
}

interface BidUser {
  id: string;
  name: string;
  avatar?: string;
}

interface Bid {
  id: string;
  price: number;
  description: string;
  status: 'PENDING' | 'ACCEPTED' | 'REJECTED' | 'WITHDRAWN';
  createdAt: Date | string;
  user: BidUser;
  images: BidImage[];
}

interface BidSectionProps {
  bids: Bid[];
  bidCount: number;
  bidsVisible: boolean;
  isOwner: boolean;
  currentUserId?: string;
}

function formatRelativeTime(date: Date | string): string {
  const now = new Date();
  const then = new Date(date);
  const diffMs = now.getTime() - then.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffMinutes = Math.floor(diffMs / (1000 * 60));

  if (diffMinutes < 60) {
    return `${diffMinutes}m ago`;
  } else if (diffHours < 24) {
    return `${diffHours}h ago`;
  } else if (diffDays === 1) {
    return 'Yesterday';
  } else if (diffDays < 7) {
    return `${diffDays}d ago`;
  } else {
    return then.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }
}

function formatPrice(price: number): string {
  if (price >= 10000000) {
    return `Rs. ${(price / 10000000).toFixed(price % 10000000 === 0 ? 0 : 1)} Crore`;
  } else if (price >= 100000) {
    return `Rs. ${(price / 100000).toFixed(price % 100000 === 0 ? 0 : 1)} Lac`;
  } else if (price >= 1000) {
    return `Rs. ${(price / 1000).toFixed(price % 1000 === 0 ? 0 : 1)} Thousand`;
  }
  return `Rs. ${price.toLocaleString()}`;
}

function getStatusBadge(status: Bid['status']) {
  switch (status) {
    case 'ACCEPTED':
      return (
        <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-100 px-2 py-0.5 rounded-full">
          <CheckCircle className="w-3 h-3" />
          Accepted
        </span>
      );
    case 'REJECTED':
      return (
        <span className="inline-flex items-center gap-1 text-xs font-medium text-red-700 bg-red-100 px-2 py-0.5 rounded-full">
          <XCircle className="w-3 h-3" />
          Rejected
        </span>
      );
    case 'WITHDRAWN':
      return (
        <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-600 bg-gray-100 px-2 py-0.5 rounded-full">
          Withdrawn
        </span>
      );
    default:
      return null;
  }
}

function BidCard({ bid, isOwner, isBidOwner }: { bid: Bid; isOwner: boolean; isBidOwner: boolean }) {
  const [showImages, setShowImages] = useState(false);
  const [showAcceptModal, setShowAcceptModal] = useState(false);
  const [showRejectConfirm, setShowRejectConfirm] = useState(false);
  const [showWithdrawConfirm, setShowWithdrawConfirm] = useState(false);
  const toast = useToast();

  const [acceptBid, { isLoading: isAccepting }] = useAcceptBidMutation();
  const [rejectBid, { isLoading: isRejecting }] = useRejectBidMutation();
  const [withdrawBid, { isLoading: isWithdrawing }] = useWithdrawBidMutation();

  const handleAccept = async () => {
    try {
      await acceptBid(bid.id).unwrap();
      toast.success('Bid Accepted', 'The ad has been closed and other bids have been rejected.');
      setShowAcceptModal(false);
    } catch {
      toast.error('Error', 'Failed to accept bid. Please try again.');
    }
  };

  const handleReject = async () => {
    try {
      await rejectBid(bid.id).unwrap();
      toast.success('Bid Rejected', 'The bid has been rejected.');
      setShowRejectConfirm(false);
    } catch {
      toast.error('Error', 'Failed to reject bid. Please try again.');
    }
  };

  const handleWithdraw = async () => {
    try {
      await withdrawBid(bid.id).unwrap();
      toast.success('Bid Withdrawn', 'Your bid has been withdrawn.');
      setShowWithdrawConfirm(false);
    } catch {
      toast.error('Error', 'Failed to withdraw bid. Please try again.');
    }
  };

  return (
    <>
      <div className="border border-gray-200 rounded-lg p-3 sm:p-4 hover:border-gray-300 transition-colors">
        {/* Header */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            {/* Avatar */}
            {bid.user.avatar ? (
              <img
                src={bid.user.avatar}
                alt={bid.user.name}
                className="w-10 h-10 rounded-full object-cover"
              />
            ) : (
              <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                <User className="w-5 h-5 text-primary-500" />
              </div>
            )}

            {/* Name & Time */}
            <div>
              <h4 className="font-medium text-gray-900">{bid.user.name}</h4>
              <div className="flex items-center gap-1 text-gray-500 text-xs">
                <Clock className="w-3 h-3" />
                <span>{formatRelativeTime(bid.createdAt)}</span>
              </div>
            </div>
          </div>

          {/* Price & Status */}
          <div className="text-right">
            <div className="text-secondary-500 font-bold text-base sm:text-lg">
              {formatPrice(bid.price)}
            </div>
            {getStatusBadge(bid.status)}
          </div>
        </div>

        {/* Description */}
        <p className="text-gray-600 text-sm mt-3 line-clamp-3">
          {bid.description}
        </p>

        {/* Images Toggle */}
        {bid.images.length > 0 && (
          <div className="mt-3">
            <button
              onClick={() => setShowImages(!showImages)}
              className="flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 font-medium"
            >
              <ImageIcon className="w-4 h-4" />
              {bid.images.length} image{bid.images.length > 1 ? 's' : ''}
              {showImages ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>

            {showImages && (
              <div className="flex gap-2 mt-2 overflow-x-auto pb-1">
                {bid.images.map((image) => (
                  <img
                    key={image.id}
                    src={image.url}
                    alt="Bid image"
                    className="w-20 h-20 rounded-lg object-cover flex-shrink-0"
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Action Buttons — only for PENDING bids */}
        {bid.status === 'PENDING' && (isOwner || isBidOwner) && (
          <div className="mt-3 pt-3 border-t border-gray-100">
            {/* Inline reject confirm */}
            {showRejectConfirm ? (
              <div className="flex items-center gap-2 text-sm">
                <span className="text-gray-600">Reject this bid?</span>
                <button
                  onClick={() => setShowRejectConfirm(false)}
                  disabled={isRejecting}
                  className="px-3 py-1.5 text-sm font-medium border border-gray-300 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  No
                </button>
                <button
                  onClick={handleReject}
                  disabled={isRejecting}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50"
                >
                  {isRejecting && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                  Yes, Reject
                </button>
              </div>
            ) : showWithdrawConfirm ? (
              <div className="flex items-center gap-2 text-sm">
                <span className="text-gray-600">Withdraw your bid?</span>
                <button
                  onClick={() => setShowWithdrawConfirm(false)}
                  disabled={isWithdrawing}
                  className="px-3 py-1.5 text-sm font-medium border border-gray-300 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  No
                </button>
                <button
                  onClick={handleWithdraw}
                  disabled={isWithdrawing}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors disabled:opacity-50"
                >
                  {isWithdrawing && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                  Yes, Withdraw
                </button>
              </div>
            ) : (
              <div className="flex flex-wrap items-center gap-2">
                {isOwner && (
                  <>
                    <button
                      onClick={() => setShowAcceptModal(true)}
                      className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg bg-green-50 text-green-600 hover:bg-green-100 transition-colors"
                    >
                      <CheckCircle className="w-4 h-4" />
                      Accept
                    </button>
                    <button
                      onClick={() => setShowRejectConfirm(true)}
                      className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg bg-red-50 text-red-600 hover:bg-red-100 transition-colors"
                    >
                      <XCircle className="w-4 h-4" />
                      Reject
                    </button>
                  </>
                )}
                {isBidOwner && (
                  <button
                    onClick={() => setShowWithdrawConfirm(true)}
                    className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Withdraw
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Accept Confirmation Modal */}
      {showAcceptModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowAcceptModal(false)}
          />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm sm:max-w-md mx-3 sm:mx-4">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <div className="flex items-center gap-2 text-green-600">
                <AlertTriangle className="w-5 h-5" />
                <h2 className="text-lg font-semibold">Accept Bid</h2>
              </div>
              <button
                onClick={() => setShowAcceptModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Body */}
            <div className="px-6 py-5">
              <p className="text-gray-600 text-sm mb-3">
                Are you sure you want to accept this bid from <strong>{bid.user.name}</strong> for <strong>{formatPrice(bid.price)}</strong>?
              </p>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-700">
                <strong>This will:</strong>
                <ul className="mt-1 ml-4 list-disc space-y-0.5">
                  <li>Close your ad (no new bids)</li>
                  <li>Reject all other pending bids</li>
                </ul>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50 rounded-b-2xl">
              <button
                onClick={() => setShowAcceptModal(false)}
                className="px-4 py-2 text-sm font-medium border border-gray-300 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAccept}
                disabled={isAccepting}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50"
              >
                {isAccepting && <Loader2 className="w-4 h-4 animate-spin" />}
                Yes, Accept Bid
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export function BidSection({ bids, bidCount, bidsVisible, isOwner, currentUserId }: BidSectionProps) {
  const [showAll, setShowAll] = useState(false);
  const displayedBids = showAll ? bids : bids.slice(0, 3);

  return (
    <div className="bg-white rounded-xl shadow-md p-3 sm:p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <MessageSquare className="w-5 h-5 text-secondary-500" />
          Bids
          <span className="text-sm font-normal text-gray-500">({bidCount} total)</span>
        </h2>
      </div>

      {!bidsVisible && !isOwner ? (
        <div className="text-center py-8 text-gray-500">
          <MessageSquare className="w-12 h-12 mx-auto mb-2 text-gray-300" />
          <p>Bids are hidden by the ad owner</p>
        </div>
      ) : bids.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <MessageSquare className="w-12 h-12 mx-auto mb-2 text-gray-300" />
          <p>No bids yet</p>
          <p className="text-sm mt-1">Be the first to submit a bid!</p>
        </div>
      ) : (
        <>
          <div className="space-y-3">
            {displayedBids.map((bid) => (
              <BidCard
                key={bid.id}
                bid={bid}
                isOwner={isOwner}
                isBidOwner={currentUserId === bid.user.id}
              />
            ))}
          </div>

          {bids.length > 3 && (
            <button
              onClick={() => setShowAll(!showAll)}
              className="w-full mt-4 py-2 text-primary-500 hover:text-primary-600 font-medium text-sm flex items-center justify-center gap-1"
            >
              {showAll ? (
                <>
                  Show Less
                  <ChevronUp className="w-4 h-4" />
                </>
              ) : (
                <>
                  Show All {bids.length} Bids
                  <ChevronDown className="w-4 h-4" />
                </>
              )}
            </button>
          )}
        </>
      )}
    </div>
  );
}
